import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";
import SelectableCell, { openSelectableCell } from "../Helpers/SelectableCell.jsx";
import GridFilterBar, {
    parsePersistedGridFilters,
    serializeGridFilters,
    useGridFilter,
} from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import useServerGridSort from "../Helpers/useServerGridSort.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { CATALOG_ITEM_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import SkuDetailDialog from "./SkuDetailDialog.jsx";
import Link from "@mui/material/Link";
import {
    SingleSelectField,
    MultiSelectField,
    LookupField,
} from "./TypedFields.jsx";
import GetData from "../../services/GetData.js";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";
import {
    catalogDataGridSx,
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
    emptySelectionModel,
    selectionIds,
} from "./catalogGridStyles.js";
import GridRowContextMenu, {
    buildCatalogItemShareUrl,
    copyTextToClipboard,
    useGridRowContextMenu,
} from "../Helpers/GridRowContextMenu.jsx";

function heroAssetFromProduct(product) {
    const photos = Array.isArray(product?.heroPhoto) ? product.heroPhoto : [];
    return photos.find((asset) => asset?.gcsObject || asset?.sourceUrl) || null;
}

function renderSkuOption(props, option) {
    const { key, ...optionProps } = props;
    const hero = heroAssetFromProduct(option);
    return (
        <Box
            component="li"
            key={key ?? option._id}
            {...optionProps}
            sx={{
                display: "flex !important",
                alignItems: "center",
                gap: 1.25,
            }}
        >
            {hero ? (
                <AuthenticatedAssetImage
                    asset={hero}
                    lazy
                    alt=""
                    placeholder=""
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                        objectFit: "cover",
                        flexShrink: 0,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f1f5f9",
                        flexShrink: 0,
                    }}
                />
            )}
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography variant="body2">
                    {option.listingField || option.itemName || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {[option.internalSkuId, option.asin].filter(Boolean).join(" · ")}
                </Typography>
            </Box>
        </Box>
    );
}

const SKU_PAGE_SIZE = 50;

function mergeProductOptions(...lists) {
    const out = [];
    const seen = new Set();
    for (const list of lists) {
        for (const option of Array.isArray(list) ? list : []) {
            const id = String(option?._id || "");
            if (!id || seen.has(id)) continue;
            seen.add(id);
            out.push(option);
        }
    }
    return out;
}

const EMPTY_FORM = {
    itemId: "",
    product: null,
    platform: "",
    status: "",
    productGrade: "",
    listPlatform: [],
    baseCost: "",
    purchaseDate: "",
    expirationDate: "",
    dateListed: "",
    needsLabel: false,
    wizardRts: false,
    notes: "",
    parserItemId: "",
    macItemSubId: "",
    auctionLink: "",
    asinPullVistaMac: "",
    amazonAsinFromBbskt: "",
    automatedInvoiceItemName: "",
    notesForAutomation: "",
    lookupAsin: "",
    lookupMsrp: "",
    lookupListingField: "",
    lookupWalmartBlocked: "",
    walmartSkuFromSku: "",
    asinFromSku: "",
    walmartBlockedFromSku: "",
    costRollup1x: "",
    costRollup2x: "",
    costRollup3x: "",
    costRollup4x: "",
    maxPurchasePriceFromSku: "",
    platformFees: "",
    totalCost: "",
    idFormula: "",
    inStock: "",
    turnTime: "",
    purchasingFlag: "",
    purchasingGap: "",
    purchasingTeamClt: "",
    uploadUserFromInvoices: "",
    invoiceNumber: "",
    orderCreationDatesFromOrders: "",
    listingFieldFromAsinLinked: "",
    asinComboBbsktAgent: "",
    walmartFlagField: "",
    shelfLocationNameRollup: "",
    shelves: "",
    shelfId: "",
    shelfBinId: "",
    durationPurchaseToProcess: "",
    durationPurchasingToSale: "",
    bundledIntoItemId: "",
    bundledIntoId: "",
    bundleSourceItemIds: "",
};

function toNumberOrUndefined(value) {
    if (value === "" || value == null) return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
}

function displayValue(value) {
    if (value == null || value === "") return "";
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    return String(value);
}

function normalizeListPlatform(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value == null || value === "") return [];
    return [String(value)].filter(Boolean);
}

function listPlatformEqual(a, b) {
    const left = normalizeListPlatform(a).slice().sort();
    const right = normalizeListPlatform(b).slice().sort();
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
}

function rowHasShelf(row) {
    if (Array.isArray(row?.shelfIds) && row.shelfIds.length > 0) return true;
    return Boolean(String(row?.shelves || "").trim());
}

function formHasShelf(form) {
    if (form?.shelfId) return true;
    return Boolean(String(form?.shelves || "").trim());
}

function ListPlatformEditCell(props) {
    const { id, field, value, api, options } = props;
    const stopEdit = () => {
        if (api.getCellMode(id, field) === "edit") {
            api.stopCellEditMode({ id, field });
        }
    };
    return (
        <Autocomplete
            multiple
            openOnFocus
            size="small"
            options={options}
            value={normalizeListPlatform(value)}
            onChange={(_event, next) => {
                api.setEditCellValue({ id, field, value: next }, _event);
            }}
            onBlur={stopEdit}
            disableCloseOnSelect
            limitTags={2}
            sx={{ width: "100%", px: 0.5 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus
                    placeholder="List platform"
                    variant="standard"
                    InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                    }}
                />
            )}
        />
    );
}
function formatDateInput(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

export default function CatalogItemGrid({
    statusFilter,
    statusNotIn,
    defaultStatus = "",
    initialSearch = "",
    fromInvoiceId = "",
    fromShipmentId = "",
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState(initialSearch || "");
    const [searchQ, setSearchQ] = useState((initialSearch || "").trim());
    const [rowCount, setRowCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState(() => emptySelectionModel());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const editingOriginalStatus = useRef("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [skuQuery, setSkuQuery] = useState("");
    const [debouncedSkuQuery, setDebouncedSkuQuery] = useState("");
    const [skuResults, setSkuResults] = useState([]);
    const [skuPage, setSkuPage] = useState(1);
    const [skuHasMore, setSkuHasMore] = useState(false);
    const [skuTotal, setSkuTotal] = useState(0);
    const [fetchingSkus, setFetchingSkus] = useState(false);
    const [loadingMoreSkus, setLoadingMoreSkus] = useState(false);
    const skuLoadMoreLock = useRef(false);
    const [saving, setSaving] = useState(false);
    const [shelfTouched, setShelfTouched] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const shareTab = searchParams.get("t") || "0";
    const rowMenuActions = useMemo(
        () => [
            {
                id: "copyShareUrl",
                label: "Copy share URL",
                disabled: (row) => !String(row?.itemId || "").trim(),
                onClick: async (row) => {
                    await copyTextToClipboard(
                        buildCatalogItemShareUrl(row, { tab: shareTab })
                    );
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    });
                },
            },
        ],
        [shareTab]
    );
    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );
    const [skuDialog, setSkuDialog] = useState({
        open: false,
        productId: null,
        product: null,
        variant: null,
    });
    /** Lightweight SKU link picker from the grid (double-click SKU cell). */
    const [linkSku, setLinkSku] = useState({
        open: false,
        catalogItemId: null,
        itemId: "",
        product: null,
    });
    const [linkSkuSaving, setLinkSkuSaving] = useState(false);
    const [listedShelfPrompt, setListedShelfPrompt] = useState(null);
    const [listedShelfId, setListedShelfId] = useState("");
    const [listedShelfBinId, setListedShelfBinId] = useState("");
    const [listedShelfSaving, setListedShelfSaving] = useState(false);
    const [listedShelfError, setListedShelfError] = useState("");
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(null);
    const bulkSaveLock = useRef(false);
    const [bulkApply, setBulkApply] = useState({
        shelf: false,
        status: false,
        listPlatform: false,
        createBundle: false,
    });
    const [bulkForm, setBulkForm] = useState({
        shelfId: "",
        shelfBinId: "",
        status: "",
        listPlatform: [],
        product: null,
    });

    useEffect(() => {
        const t = setTimeout(() => {
            const nextQ = searchInput.trim();
            setSearchQ(nextQ);
            setPaginationModel((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 }
            );

            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (nextQ) next.set("q", nextQ);
                    else next.delete("q");
                    if (fromInvoiceId) next.set("fromInvoice", fromInvoiceId);
                    else next.delete("fromInvoice");
                    if (fromShipmentId) next.set("fromShipment", fromShipmentId);
                    else next.delete("fromShipment");
                    if (next.toString() === prev.toString()) return prev;
                    return next;
                },
                { replace: true }
            );
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput, fromInvoiceId, fromShipmentId, setSearchParams]);

    const { data: fieldMeta } = useApiData("api/broylesllc/catalog-field-meta");
    const catalogMeta = fieldMeta?.catalogItem || {};
    const filterFields = useMemo(() => CATALOG_ITEM_FILTER_FIELDS(catalogMeta), [catalogMeta]);
    const initialGridFilters = useMemo(
        () => parsePersistedGridFilters(searchParams.get("gf")),
        // Hydrate once from the URL on mount (per tab remount).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const {
        activeConditions,
        conditions: gridConditions,
        conjunction: gridConjunction,
        open: gridFiltersOpen,
        barProps: filterBar,
    } = useGridFilter(filterFields, { initial: initialGridFilters });

    // Persist grid filters in the URL so refresh / tab switches keep them.
    useEffect(() => {
        const serialized = serializeGridFilters({
            conjunction: gridConjunction,
            conditions: gridConditions,
            open: gridFiltersOpen,
        });
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (serialized) next.set("gf", serialized);
                else next.delete("gf");
                if (next.toString() === prev.toString()) return prev;
                return next;
            },
            { replace: true }
        );
    }, [gridConjunction, gridConditions, gridFiltersOpen, setSearchParams]);

    const serverFilterConditions = useMemo(
        () =>
            activeConditions.filter((c) => {
                // Tab status wins over grid status filters.
                if (statusFilter && c.field === "status") return false;
                return true;
            }),
        [activeConditions, statusFilter]
    );

    const filtersParam = useMemo(() => {
        if (!serverFilterConditions.length) return "";
        return JSON.stringify({
            conjunction: gridConjunction,
            conditions: serverFilterConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [serverFilterConditions, gridConjunction]);

    const catalogFilterQuery = useMemo(() => {
        const params = new URLSearchParams();
        if (searchQ) params.set("q", searchQ);
        if (fromInvoiceId) params.set("invoiceId", fromInvoiceId);
        if (statusFilter) {
            params.set("status", statusFilter);
        } else if (Array.isArray(statusNotIn) && statusNotIn.length) {
            params.set("statusNotIn", statusNotIn.join(","));
        }
        if (filtersParam) params.set("filters", filtersParam);
        return params.toString();
    }, [searchQ, statusFilter, statusNotIn, fromInvoiceId, filtersParam]);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.catalogItems, {
        sortModel: [{ field: "itemId", sort: "asc" }],
        columnVisibilityModel: {},
    });
    const { sortModel, sortForApi, onSortModelChange } = useServerGridSort(
        layout,
        "itemId",
        "asc"
    );

    const listUrl = useMemo(() => {
        const params = new URLSearchParams(catalogFilterQuery);
        params.set("page", String(paginationModel.page + 1));
        params.set("pageSize", String(paginationModel.pageSize));
        params.set("sort", sortForApi.sort);
        params.set("sortDir", sortForApi.sortDir);
        return `api/broylesllc/catalog-items?${params.toString()}`;
    }, [
        catalogFilterQuery,
        paginationModel.page,
        paginationModel.pageSize,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);
    const { data: shelfOptions } = useApiData("api/broylesllc/shelf-options", {
        enabled: dialogOpen || bulkOpen || Boolean(listedShelfPrompt),
    });
    const shelfChoices = Array.isArray(shelfOptions) ? shelfOptions : [];

    const skuPickerOpen =
        dialogOpen || linkSku.open || (bulkOpen && bulkApply.createBundle);

    useEffect(() => {
        if (!skuPickerOpen) return undefined;
        const t = setTimeout(() => setDebouncedSkuQuery(skuQuery.trim()), 300);
        return () => clearTimeout(t);
    }, [skuQuery, skuPickerOpen]);

    useEffect(() => {
        if (!skuPickerOpen || !debouncedSkuQuery) {
            setSkuResults([]);
            setSkuPage(1);
            setSkuHasMore(false);
            setSkuTotal(0);
            setFetchingSkus(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setFetchingSkus(true);
            setSkuPage(1);
            setSkuHasMore(false);
            try {
                const { data } = await api.get("/api/broylesllc/products", {
                    params: {
                        q: debouncedSkuQuery,
                        page: 1,
                        pageSize: SKU_PAGE_SIZE,
                        includeAssets: 1,
                    },
                });
                if (cancelled) return;
                const items = Array.isArray(data?.items) ? data.items : [];
                const total = Number(data?.total) || items.length;
                const page = Number(data?.page) || 1;
                const pageSize = Number(data?.pageSize) || SKU_PAGE_SIZE;
                setSkuResults(items);
                setSkuTotal(total);
                setSkuHasMore(page * pageSize < total);
                setSkuPage(1);
            } catch {
                if (!cancelled) {
                    setSkuResults([]);
                    setSkuTotal(0);
                    setSkuHasMore(false);
                }
            } finally {
                if (!cancelled) setFetchingSkus(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [debouncedSkuQuery, skuPickerOpen]);

    const loadMoreSkus = useCallback(async () => {
        if (
            !skuPickerOpen ||
            !debouncedSkuQuery ||
            !skuHasMore ||
            fetchingSkus ||
            loadingMoreSkus ||
            skuLoadMoreLock.current
        ) {
            return;
        }
        skuLoadMoreLock.current = true;
        setLoadingMoreSkus(true);
        const nextPage = skuPage + 1;
        try {
            const { data } = await api.get("/api/broylesllc/products", {
                params: {
                    q: debouncedSkuQuery,
                    page: nextPage,
                    pageSize: SKU_PAGE_SIZE,
                    includeAssets: 1,
                },
            });
            const items = Array.isArray(data?.items) ? data.items : [];
            const total = Number(data?.total) || 0;
            const page = Number(data?.page) || nextPage;
            const pageSize = Number(data?.pageSize) || SKU_PAGE_SIZE;
            setSkuResults((prev) => mergeProductOptions(prev, items));
            setSkuTotal(total);
            setSkuHasMore(page * pageSize < total);
            setSkuPage(nextPage);
        } catch {
            /* keep current list */
        } finally {
            setLoadingMoreSkus(false);
            skuLoadMoreLock.current = false;
        }
    }, [
        skuPickerOpen,
        debouncedSkuQuery,
        skuHasMore,
        fetchingSkus,
        loadingMoreSkus,
        skuPage,
    ]);

    const skuListboxProps = useMemo(
        () => ({
            style: { maxHeight: 260 },
            onScroll: (event) => {
                const el = event.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
                    loadMoreSkus();
                }
            },
        }),
        [loadMoreSkus]
    );

    const skuHelperText = useMemo(() => {
        if (debouncedSkuQuery && skuTotal > 0) {
            return `Showing ${Math.min(skuResults.length, skuTotal)} of ${skuTotal} — scroll for more`;
        }
        return "";
    }, [debouncedSkuQuery, skuResults.length, skuTotal]);

    const skuOptions = useMemo(() => {
        const current = dialogOpen
            ? form.product
            : linkSku.open
              ? linkSku.product
              : bulkForm.product;
        return mergeProductOptions(current ? [current] : [], skuResults);
    }, [
        skuResults,
        form.product,
        linkSku.product,
        linkSku.open,
        dialogOpen,
        bulkForm.product,
    ]);

    const formShelfBins = useMemo(() => {
        const selected = shelfChoices.find((option) => option.id === form.shelfId);
        return Array.isArray(selected?.bins) ? selected.bins : [];
    }, [shelfChoices, form.shelfId]);
    const bulkShelfBins = useMemo(() => {
        const selected = shelfChoices.find((option) => option.id === bulkForm.shelfId);
        return Array.isArray(selected?.bins) ? selected.bins : [];
    }, [shelfChoices, bulkForm.shelfId]);
    const listedShelfBins = useMemo(() => {
        const selected = shelfChoices.find((option) => option.id === listedShelfId);
        return Array.isArray(selected?.bins) ? selected.bins : [];
    }, [shelfChoices, listedShelfId]);
    const listPlatformChoices = useMemo(
        () =>
            (catalogMeta.listPlatform?.choices || []).filter(
                (choice) => choice && choice !== "e"
            ),
        [catalogMeta.listPlatform?.choices]
    );

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : 0);
        setRows(
            items.map((c) => ({
                id: c._id,
                itemId: c.itemId || "",
                sku:
                    c.product?.listingField ||
                    c.product?.internalSkuId ||
                    c.walmartSkuFromSku ||
                    "",
                invoiceNumber: c.invoiceNumber || "",
                invoiceId: c.invoice ? String(c.invoice) : "",
                platform: c.platform || "",
                status: c.status || "",
                productGrade: c.productGrade || "",
                listPlatform: normalizeListPlatform(c.listPlatform),
                listPlatformText: displayValue(c.listPlatform),
                macItemSubId: c.macItemSubId == null ? "" : c.macItemSubId,
                purchaseDate: c.purchaseDate
                    ? new Date(c.purchaseDate).toLocaleDateString()
                    : "",
                purchaseDateRaw: c.purchaseDate || null,
                expirationDate: c.expirationDate
                    ? new Date(c.expirationDate).toLocaleDateString()
                    : "",
                expirationDateRaw: c.expirationDate || null,
                dateListed: c.dateListed
                    ? new Date(c.dateListed).toLocaleDateString()
                    : "",
                dateListedRaw: c.dateListed || null,
                baseCost: c.baseCost ?? "",
                purchasingGap: c.purchasingGap ?? "",
                purchasingFlag: c.purchasingFlag || "",
                needsLabel: Boolean(c.needsLabel),
                notes: c.notes || "",
                productId: c.product?._id || null,
                product: c.product || null,
                variant: c.variant || null,
                shelfIds: Array.isArray(c.shelfIds) ? c.shelfIds.map(String) : [],
                shelves: Array.isArray(c.shelves) ? c.shelves.join(", ") : "",
                shelfBinId: c.shelfBinId ? String(c.shelfBinId) : "",
                shelfBinName: c.shelfBinName || "",
                bundledIntoItemId: c.bundleInfo?.bundledIntoItemId || "",
                bundledIntoId: c.bundleInfo?.bundledInto
                    ? String(c.bundleInfo.bundledInto)
                    : "",
                bundleSourceItemIds: Array.isArray(c.bundleInfo?.sourceItems)
                    ? c.bundleInfo.sourceItems
                          .map((entry) => entry?.itemId)
                          .filter(Boolean)
                          .join(", ")
                    : "",
                raw: c,
            }))
        );
    }, [data]);

    /** Select-all (exclude mode) applies to the current grid page only. */
    const selectedIds = useMemo(
        () => selectionIds(selectionModel, rows),
        [selectionModel, rows]
    );

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [catalogFilterQuery, sortForApi.sort, sortForApi.sortDir]);

    const openSkuDialog = (row) => {
        if (!row?.productId && !row?.product && !row?.sku) return;
        setSkuDialog({
            open: true,
            productId: row.productId,
            product: row.product,
            variant: row.variant,
        });
    };

    const productFromRow = (row) => {
        const prod = row?.product && typeof row.product === "object" ? row.product : null;
        if (!prod?._id) return null;
        return {
            _id: prod._id,
            listingField: prod.listingField || row.sku || "",
            internalSkuId: prod.internalSkuId || "",
            itemName: prod.itemName || "",
            asin: prod.asin || "",
        };
    };

    const openLinkSku = (row) => {
        if (!row?.id) return;
        setSkuQuery("");
        setDebouncedSkuQuery("");
        setLinkSku({
            open: true,
            catalogItemId: row.id,
            itemId: row.itemId || "",
            product: productFromRow(row),
        });
    };

    const focusBundledItem = (itemId) => {
        const label = String(itemId || "").trim();
        if (!label) return;
        setSearchInput(label);
        setSnack({
            open: true,
            color: "info",
            message: `Searching for bundle ${label}.`,
        });
    };

    const closeLinkSku = ({ force = false } = {}) => {
        if (linkSkuSaving && !force) return;
        setLinkSku({
            open: false,
            catalogItemId: null,
            itemId: "",
            product: null,
        });
        setSkuQuery("");
        setDebouncedSkuQuery("");
    };

    const saveLinkSku = async () => {
        if (!linkSku.catalogItemId) return;
        setLinkSkuSaving(true);
        const result = await PutData(
            { product: linkSku.product?._id || null },
            `broylesllc/catalog-items/${linkSku.catalogItemId}`
        );
        setLinkSkuSaving(false);
        if (result.success) {
            setSnack({
                open: true,
                color: "success",
                message: linkSku.product ? "SKU linked." : "SKU unlinked.",
            });
            closeLinkSku({ force: true });
            refetch();
        } else {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Could not update SKU link.",
            });
        }
    };

    const baseColumns = useMemo(
        () => [
            {
                field: "itemId",
                headerName: "Item ID",
                minWidth: 150,
                flex: 0.8,
            },
            {
                field: "invoiceNumber",
                headerName: "Invoice",
                minWidth: 170,
                flex: 0.8,
                renderCell: (params) => {
                    const label = params.value || "";
                    if (!label) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    const invoiceId = params.row.invoiceId;
                    if (!invoiceId) return label;
                    const invoiceHref = `/invoices?id=${encodeURIComponent(invoiceId)}${
                        label ? `&n=${encodeURIComponent(label)}` : ""
                    }`;
                    return (
                        <Link
                            component={RouterLink}
                            to={invoiceHref}
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                        >
                            {label}
                        </Link>
                    );
                },
            },
            {
                field: "sku",
                headerName: "SKU",
                minWidth: 180,
                flex: 1.2,
                renderCell: (params) => {
                    const label = params.value || "";
                    if (!label) {
                        return (
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openLinkSku(params.row);
                                }}
                                sx={{
                                    fontSize: 12,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontWeight: 700,
                                    color: "#b45309",
                                    backgroundColor: "#fff6e5",
                                    border: "1px solid #f6d787",
                                    borderRadius: "8px",
                                    px: 1,
                                    py: 0.25,
                                    lineHeight: 1.4,
                                    "&:hover": {
                                        backgroundColor: "#ffedd5",
                                        borderColor: "#f0c14b",
                                        color: "#92400e",
                                    },
                                }}
                            >
                                Add SKU
                            </Link>
                        );
                    }
                    const clickable = Boolean(params.row.productId || params.row.product);
                    if (!clickable) return label;
                    return (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            title="Click to view · double-click to change"
                            onClick={(e) => {
                                e.stopPropagation();
                                openSkuDialog(params.row);
                            }}
                            sx={{
                                fontSize: "inherit",
                                textAlign: "left",
                                cursor: "pointer",
                                fontWeight: 600,
                                color: "#0d9488",
                            }}
                        >
                            {label}
                        </Link>
                    );
                },
            },
            {
                field: "shelves",
                headerName: "Shelf",
                minWidth: 160,
                flex: 0.9,
                sortable: false,
                valueGetter: (_value, row) => {
                    const shelf = row.shelves || "";
                    const bin = row.shelfBinName || "";
                    if (shelf && bin) return `${shelf} · ${bin}`;
                    return shelf || bin || "";
                },
            },
            { field: "platform", headerName: "Platform", minWidth: 160, flex: 0.7,
                type: "singleSelect",
                editable: true,
                valueOptions: catalogMeta.platform?.choices || [],
                renderCell: (params) => <SelectableCell value={params.value} />,
            },
            {
                field: "macItemSubId",
                headerName: "MAC Item Sub ID",
                minWidth: 140,
                flex: 0.5,
                valueGetter: (_value, row) =>
                    row.macItemSubId == null || row.macItemSubId === ""
                        ? ""
                        : String(row.macItemSubId),
            },
            {
                field: "status",
                headerName: "Status",
                minWidth: 160,
                flex: 0.7,
                type: "singleSelect",
                editable: true,
                valueOptions: catalogMeta.status?.choices || [],
                renderCell: (params) => <SelectableCell value={params.value} />,
            },
            {
                field: "bundledIntoItemId",
                headerName: "Bundled Into",
                minWidth: 150,
                flex: 0.7,
                sortable: false,
                renderCell: (params) => {
                    const label = params.value || "";
                    if (!label) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={(e) => {
                                e.stopPropagation();
                                focusBundledItem(label);
                            }}
                            sx={{
                                fontSize: "inherit",
                                textAlign: "left",
                                cursor: "pointer",
                                fontWeight: 600,
                                color: "#0d9488",
                            }}
                        >
                            {label}
                        </Link>
                    );
                },
            },
            {
                field: "listPlatform",
                headerName: "List Platform",
                minWidth: 180,
                flex: 0.9,
                editable: true,
                sortable: false,
                valueGetter: (_value, row) => normalizeListPlatform(row.listPlatform),
                valueFormatter: (value) => displayValue(value),
                renderCell: (params) => (
                    <SelectableCell value={displayValue(params.value)} />
                ),
                renderEditCell: (params) => (
                    <ListPlatformEditCell
                        {...params}
                        options={(catalogMeta.listPlatform?.choices || []).filter(
                            (choice) => choice && choice !== "e"
                        )}
                    />
                ),
            },
            { field: "productGrade", headerName: "Grade", minWidth: 120,
                type: "singleSelect",
                editable: true,
                valueOptions: catalogMeta.productGrade?.choices || [],
                renderCell: (params) => <SelectableCell value={params.value} />,
            },
            { field: "purchaseDate", headerName: "Purchase Date", minWidth: 120 },
            { field: "expirationDate", headerName: "Expiration Date", minWidth: 130 },
            { field: "dateListed", headerName: "Date Listed", minWidth: 120 },
            {
                field: "baseCost",
                headerName: "Base Cost",
                width: 100,
                type: "number",
                align: "right",
                headerAlign: "right",
            },
            {
                field: "purchasingGap",
                headerName: "Purchasing Gap",
                width: 130,
                type: "number",
                align: "right",
                headerAlign: "right",
            },
            {
                field: "purchasingFlag",
                headerName: "Purchasing Flag",
                minWidth: 130,
                width: 140,
                type: "singleSelect",
                valueOptions: ["FLAG", "GOOD"],
                renderCell: (params) => {
                    const value = String(params.value || "").trim();
                    if (!value) {
                        return (
                            <Typography
                                component="span"
                                sx={{ color: "#9aa3ad", fontSize: 13 }}
                            >
                                —
                            </Typography>
                        );
                    }
                    const isFlag = value === "FLAG";
                    return (
                        <Chip
                            size="small"
                            label={value}
                            sx={{
                                height: 24,
                                fontWeight: 600,
                                fontSize: 12,
                                borderRadius: "8px",
                                backgroundColor: isFlag ? "#fef2f2" : "#ecfdf5",
                                color: isFlag ? "#b91c1c" : "#047857",
                            }}
                        />
                    );
                },
            },
            {
                field: "needsLabel",
                headerName: "Needs Label",
                width: 120,
                renderCell: (params) =>
                    params.value ? (
                        <Chip
                            size="small"
                            label="Yes"
                            sx={{
                                height: 24,
                                fontWeight: 600,
                                fontSize: 12,
                                borderRadius: "8px",
                                backgroundColor: "#fff6e5",
                                color: "#b45309",
                            }}
                        />
                    ) : (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            No
                        </Typography>
                    ),
            },
        ],
        [catalogMeta]
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    const openCreate = () => {
        setEditingId(null);
        editingOriginalStatus.current = "";
        setShelfTouched(false);
        setSkuQuery("");
        setDebouncedSkuQuery("");
        setForm({
            ...EMPTY_FORM,
            status: defaultStatus || statusFilter || "",
        });
        setDialogOpen(true);
    };

    const applySkuSelection = (next) => {
        setForm((prev) => ({
            ...prev,
            product: next
                ? {
                      _id: next._id,
                      listingField: next.listingField || "",
                      internalSkuId: next.internalSkuId || "",
                      itemName: next.itemName || "",
                      asin: next.asin || "",
                      msrpUsd: next.msrpUsd,
                      walmartBlocked: next.walmartBlocked || "",
                  }
                : null,
            lookupAsin: next?.asin || "",
            lookupMsrp: next?.msrpUsd ?? "",
            lookupListingField: next?.listingField || "",
            lookupWalmartBlocked: next?.walmartBlocked || "",
            walmartSkuFromSku: next?.internalSkuId || "",
            asinFromSku: next?.asin || "",
            walmartBlockedFromSku: next?.walmartBlocked || "",
            listingFieldFromAsinLinked: next?.listingField || "",
        }));
    };

    const openEdit = async (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        if (!id) return;
        const { getData } = await GetData(`broylesllc/catalog-items/${id}`);
        if (!getData) {
            setSnack({
                open: true,
                color: "error",
                message: "Could not load catalog item.",
            });
            return;
        }
        const c = getData;
        const prod = c.product && typeof c.product === "object" ? c.product : null;
        const shelfIds = Array.isArray(c.shelfIds) ? c.shelfIds : [];
        setEditingId(c._id);
        editingOriginalStatus.current = c.status || "";
        setShelfTouched(false);
        setSkuQuery("");
        setDebouncedSkuQuery("");
        setForm({
            itemId: c.itemId || "",
            product: prod
                ? {
                      _id: prod._id,
                      listingField: prod.listingField || "",
                      internalSkuId: prod.internalSkuId || "",
                      itemName: prod.itemName || "",
                      asin: prod.asin || "",
                      msrpUsd: prod.msrpUsd,
                      walmartBlocked: prod.walmartBlocked || "",
                  }
                : null,
            platform: c.platform || "",
            status: c.status || "",
            productGrade: c.productGrade || "",
            listPlatform: Array.isArray(c.listPlatform) ? c.listPlatform : [],
            baseCost: c.baseCost == null ? "" : String(c.baseCost),
            purchaseDate: formatDateInput(c.purchaseDate),
            expirationDate: formatDateInput(c.expirationDate),
            dateListed: c.dateListed
                ? new Date(c.dateListed).toLocaleDateString()
                : "",
            needsLabel: Boolean(c.needsLabel),
            wizardRts: Boolean(c.wizardRts),
            notes: c.notes || "",
            parserItemId: c.parserItemId || "",
            macItemSubId: c.macItemSubId == null ? "" : String(c.macItemSubId),
            auctionLink: c.auctionLink || "",
            asinPullVistaMac: c.asinPullVistaMac || "",
            amazonAsinFromBbskt: c.amazonAsinFromBbskt || "",
            automatedInvoiceItemName: c.automatedInvoiceItemName || "",
            notesForAutomation: c.notesForAutomation || "",
            lookupAsin: prod?.asin || c.asinFromSku || "",
            lookupMsrp: prod?.msrpUsd ?? "",
            lookupListingField: prod?.listingField || "",
            lookupWalmartBlocked: prod?.walmartBlocked || c.walmartBlockedFromSku || "",
            walmartSkuFromSku: c.walmartSkuFromSku || prod?.internalSkuId || "",
            asinFromSku: c.asinFromSku || prod?.asin || "",
            walmartBlockedFromSku: c.walmartBlockedFromSku || "",
            costRollup1x: displayValue(c.costRollup1x),
            costRollup2x: displayValue(c.costRollup2x),
            costRollup3x: displayValue(c.costRollup3x),
            costRollup4x: displayValue(c.costRollup4x),
            maxPurchasePriceFromSku: displayValue(c.maxPurchasePriceFromSku),
            platformFees: displayValue(c.platformFees),
            totalCost: displayValue(c.totalCost),
            idFormula: c.idFormula || "",
            inStock: displayValue(c.inStock),
            turnTime: displayValue(c.turnTime),
            purchasingFlag: c.purchasingFlag || "",
            purchasingGap: displayValue(c.purchasingGap),
            purchasingTeamClt: displayValue(c.purchasingTeamClt),
            uploadUserFromInvoices: c.uploadUserFromInvoices || "",
            invoiceNumber: c.invoiceNumber || "",
            orderCreationDatesFromOrders: Array.isArray(c.orderCreationDatesFromOrders)
                ? c.orderCreationDatesFromOrders.map((d) => formatDateInput(d)).filter(Boolean).join(", ")
                : "",
            listingFieldFromAsinLinked: c.listingFieldFromAsinLinked || "",
            asinComboBbsktAgent: c.asinComboBbsktAgent || "",
            walmartFlagField: c.walmartFlagField || "",
            shelves: displayValue(c.shelves),
            shelfId: shelfIds.length === 1 ? shelfIds[0] : "",
            shelfBinId: c.shelfBinId ? String(c.shelfBinId) : "",
            shelfLocationNameRollup: displayValue(c.shelfLocationNameRollup),
            durationPurchaseToProcess: displayValue(c.durationPurchaseToProcess),
            durationPurchasingToSale: displayValue(c.durationPurchasingToSale),
            bundledIntoItemId: c.bundleInfo?.bundledIntoItemId || "",
            bundledIntoId: c.bundleInfo?.bundledInto
                ? String(c.bundleInfo.bundledInto)
                : "",
            bundleSourceItemIds: Array.isArray(c.bundleInfo?.sourceItems)
                ? c.bundleInfo.sourceItems
                      .map((entry) => entry?.itemId)
                      .filter(Boolean)
                      .join(", ")
                : "",
        });
        setDialogOpen(true);
    };

    const setField = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const setSelect = (key) => (value) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        if (!form.itemId.trim()) {
            setSnack({
                open: true,
                color: "error",
                message: "Item ID is required.",
            });
            return;
        }
        const clearingShelf = shelfTouched && !form.shelfId;
        const becomingListed =
            form.status === "Listed" &&
            Boolean(editingId) &&
            editingOriginalStatus.current !== "Listed";
        // Transitioning into Listed still requires a shelf (same as grid prompt).
        // Already-Listed items without a shelf may be saved with a warning.
        if (
            form.status === "Listed" &&
            (clearingShelf || !formHasShelf(form)) &&
            (becomingListed || !editingId)
        ) {
            setSnack({
                open: true,
                color: "warning",
                message: "Add a shelf location before setting status to Listed.",
            });
            return;
        }
        const warnListedWithoutShelf =
            form.status === "Listed" && (clearingShelf || !formHasShelf(form));
        setSaving(true);
        const payload = {
            itemId: form.itemId.trim(),
            product: form.product?._id || null,
            platform: form.platform,
            status: form.status,
            productGrade: form.productGrade,
            listPlatform: form.listPlatform,
            baseCost: toNumberOrUndefined(form.baseCost),
            purchaseDate: form.purchaseDate
                ? new Date(form.purchaseDate).toISOString()
                : null,
            expirationDate: form.expirationDate
                ? new Date(form.expirationDate).toISOString()
                : null,
            needsLabel: Boolean(form.needsLabel),
            wizardRts: Boolean(form.wizardRts),
            notes: form.notes,
            parserItemId: form.parserItemId,
            macItemSubId: String(form.macItemSubId || "").trim(),
            auctionLink: form.auctionLink,
            asinPullVistaMac: form.asinPullVistaMac,
            amazonAsinFromBbskt: form.amazonAsinFromBbskt,
            automatedInvoiceItemName: form.automatedInvoiceItemName,
            notesForAutomation: form.notesForAutomation,
        };
        if (shelfTouched || (!editingId && form.shelfId) || (form.status === "Listed" && form.shelfId)) {
            payload.shelfId = form.shelfId || null;
            payload.shelfBinId = form.shelfId ? form.shelfBinId || null : null;
        } else if (editingId && form.shelfId) {
            // Bin can change without re-picking the shelf.
            payload.shelfBinId = form.shelfBinId || null;
        }

        const result = editingId
            ? await PutData(payload, `broylesllc/catalog-items/${editingId}`)
            : await PostData(payload, "broylesllc/catalog-items");

        setSaving(false);
        if (result.success) {
            setDialogOpen(false);
            if (warnListedWithoutShelf || result?.data?.listedWithoutShelf) {
                setSnack({
                    open: true,
                    color: "warning",
                    message:
                        "Saved. This item is Listed with no shelf location — add a shelf when you can.",
                });
            } else {
                setSnack({
                    open: true,
                    color: "success",
                    message: editingId ? "Catalog item updated." : "Catalog item created.",
                });
            }
            refetch();
            setSelectionModel({ type: "include", ids: new Set() });
        } else {
            const msg =
                result?.data?.message ||
                result?.error?.response?.data?.message ||
                "Save failed.";
            setSnack({ open: true, color: "error", message: msg });
        }
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)))
            .map((row) => ({
                key: String(row.id),
                label: row.itemId || String(row.id),
            }));
    }, [rows, selectedIds]);

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(
                selectedIds.map((id) =>
                    api.delete(`api/broylesllc/catalog-items/${id}`)
                )
            );
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    const openBulkEdit = () => {
        if (!selectedIds.length || bulkSaving) return;
        setBulkApply({
            shelf: false,
            status: false,
            listPlatform: false,
            createBundle: false,
        });
        setBulkForm({
            shelfId: "",
            shelfBinId: "",
            status: "",
            listPlatform: [],
            product: null,
        });
        setSkuQuery("");
        setDebouncedSkuQuery("");
        setBulkProgress(null);
        setBulkOpen(true);
    };

    const closeBulkEdit = () => {
        if (bulkSaving) return;
        setBulkOpen(false);
        setBulkProgress(null);
    };

    const saveBulk = async () => {
        if (bulkSaveLock.current || bulkSaving) return;
        if (!selectedIds.length) return;

        if (bulkApply.createBundle) {
            if (selectedIds.length < 2) {
                setSnack({
                    open: true,
                    color: "warning",
                    message: "Select at least two items to create a bundle.",
                });
                return;
            }
            if (!bulkForm.product?._id) {
                setSnack({
                    open: true,
                    color: "warning",
                    message: "Product SKU is required to create a bundle.",
                });
                return;
            }

            const ids = selectedIds.map(String);
            bulkSaveLock.current = true;
            setBulkSaving(true);
            setBulkProgress({ done: 0, total: ids.length });
            try {
                const result = await PostData(
                    { ids, product: String(bulkForm.product._id) },
                    "broylesllc/catalog-items/create-bundle"
                );
                if (!result.success) {
                    setSnack({
                        open: true,
                        color: "error",
                        message:
                            result?.error?.response?.data?.message ||
                            result?.data?.message ||
                            "Could not create bundle.",
                    });
                    return;
                }
                const bundleItemId = result.data?.bundle?.itemId || "";
                await refetch();
                setSelectionModel(emptySelectionModel());
                setBulkOpen(false);
                setBulkProgress(null);
                setSnack({
                    open: true,
                    color: "success",
                    message: bundleItemId
                        ? `Created bundle ${bundleItemId} from ${ids.length} items.`
                        : `Created bundle from ${ids.length} items.`,
                });
            } finally {
                setBulkSaving(false);
                bulkSaveLock.current = false;
            }
            return;
        }

        if (!bulkApply.shelf && !bulkApply.status && !bulkApply.listPlatform) {
            setSnack({
                open: true,
                color: "warning",
                message: "Check at least one field to update.",
            });
            return;
        }

        const ids = selectedIds.map(String);
        const selectedIdSet = new Set(ids);
        const selectedRows = rows.filter((row) => selectedIdSet.has(String(row.id)));
        const clearingShelf = bulkApply.shelf && !bulkForm.shelfId;
        const settingListed = bulkApply.status && bulkForm.status === "Listed";
        const anySelectedWithoutShelf = selectedRows.some((row) => !rowHasShelf(row));

        if (clearingShelf) {
            const wouldLeaveListed =
                settingListed ||
                (!bulkApply.status &&
                    selectedRows.some((row) => row.status === "Listed"));
            if (wouldLeaveListed) {
                setSnack({
                    open: true,
                    color: "warning",
                    message: "Cannot clear shelf while status is Listed.",
                });
                return;
            }
        }

        if (settingListed) {
            const shelfCovered = bulkApply.shelf && Boolean(bulkForm.shelfId);
            if (!shelfCovered && anySelectedWithoutShelf) {
                setSnack({
                    open: true,
                    color: "warning",
                    message:
                        "Choose a shelf for items without one before setting status to Listed.",
                });
                return;
            }
        }

        const fieldPayload = {};
        if (bulkApply.shelf) {
            fieldPayload.shelfId = bulkForm.shelfId || null;
            fieldPayload.shelfBinId = bulkForm.shelfId
                ? bulkForm.shelfBinId || null
                : null;
        }
        if (bulkApply.status) fieldPayload.status = bulkForm.status || "";
        if (bulkApply.listPlatform) {
            fieldPayload.listPlatform = normalizeListPlatform(bulkForm.listPlatform);
        }

        const BATCH = 25;
        bulkSaveLock.current = true;
        setBulkSaving(true);
        setBulkProgress({ done: 0, total: ids.length });

        let updated = 0;
        let failedCount = 0;
        let hardError = "";

        try {
            for (let i = 0; i < ids.length; i += BATCH) {
                const chunk = ids.slice(i, i + BATCH);
                const result = await PostData(
                    { ...fieldPayload, ids: chunk },
                    "broylesllc/catalog-items/bulk-update"
                );
                if (!result.success) {
                    hardError =
                        result?.error?.response?.data?.message ||
                        result?.data?.message ||
                        "Bulk update failed.";
                    break;
                }
                updated += Number(result.data?.updated) || 0;
                failedCount += Array.isArray(result.data?.failed)
                    ? result.data.failed.length
                    : 0;
                setBulkProgress({
                    done: Math.min(i + chunk.length, ids.length),
                    total: ids.length,
                });
            }

            if (hardError && updated === 0) {
                setSnack({ open: true, color: "error", message: hardError });
                return;
            }

            await refetch();
            setSelectionModel(emptySelectionModel());
            setBulkOpen(false);
            setBulkProgress(null);

            if (hardError) {
                setSnack({
                    open: true,
                    color: "warning",
                    message: `Updated ${updated} of ${ids.length}; stopped early: ${hardError}`,
                });
            } else if (failedCount) {
                setSnack({
                    open: true,
                    color: "warning",
                    message: `Updated ${updated}; ${failedCount} failed (Listed needs a shelf).`,
                });
            } else {
                setSnack({
                    open: true,
                    color: "success",
                    message: `Updated ${updated} item${updated === 1 ? "" : "s"}.`,
                });
            }
        } finally {
            setBulkSaving(false);
            bulkSaveLock.current = false;
        }
    };

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    Failed to load catalog items.
                </Typography>
            )}
            <div className="catalog-grid-shell">
                {fromInvoiceId || fromShipmentId ? (
                    <div
                        className="catalog-toolbar"
                        style={{ borderBottom: "none", paddingBottom: 0 }}
                    >
                        <Button
                            size="small"
                            component={RouterLink}
                            to={
                                fromInvoiceId
                                    ? `/invoices?id=${encodeURIComponent(fromInvoiceId)}${
                                          searchParams.get("n")
                                              ? `&n=${encodeURIComponent(searchParams.get("n"))}`
                                              : rows[0]?.invoiceNumber
                                                ? `&n=${encodeURIComponent(rows[0].invoiceNumber)}`
                                                : ""
                                      }`
                                    : "/order-shipping"
                            }
                            startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                            sx={catalogQuietButtonSx}
                        >
                            {fromInvoiceId ? "Back to invoice" : "Back to shipping"}
                        </Button>
                    </div>
                ) : null}
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search Item ID, invoice, or status…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">
                            {selectedIds.length} selected
                        </span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || bulkSaving}
                        startIcon={<EditNoteOutlinedIcon fontSize="small" />}
                        onClick={openBulkEdit}
                        sx={catalogQuietButtonSx}
                    >
                        Bulk Edit
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add Item
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading)}
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onCellClick={(params) =>
                            openSelectableCell(params, [
                                "platform",
                                "status",
                                "listPlatform",
                                "productGrade",
                            ])
                        }
                        onCellDoubleClick={(params) => {
                            if (
                                ["platform", "status", "listPlatform", "productGrade"].includes(
                                    params.field
                                )
                            ) {
                                return;
                            }
                            if (params.field === "sku") {
                                openLinkSku(params.row);
                                return;
                            }
                            openEdit(params.id);
                        }}
                        processRowUpdate={async (newRow, oldRow) => {
                            const payload = {};
                            for (const key of ["platform", "status", "productGrade"]) {
                                if ((newRow[key] || "") !== (oldRow[key] || "")) {
                                    payload[key] = newRow[key] || "";
                                }
                            }
                            const nextListPlatform = normalizeListPlatform(newRow.listPlatform);
                            if (!listPlatformEqual(nextListPlatform, oldRow.listPlatform)) {
                                payload.listPlatform = nextListPlatform;
                            }
                            if (!Object.keys(payload).length) return oldRow;

                            const becomingListed =
                                payload.status === "Listed" && oldRow.status !== "Listed";
                            if (becomingListed && !rowHasShelf(newRow) && !rowHasShelf(oldRow)) {
                                return new Promise((resolve, reject) => {
                                    setListedShelfId("");
                                    setListedShelfBinId("");
                                    setListedShelfError("");
                                    setListedShelfPrompt({
                                        catalogItemId: newRow.id,
                                        itemLabel: newRow.itemId || "this item",
                                        pendingPayload: payload,
                                        pendingRow: {
                                            ...newRow,
                                            listPlatform: nextListPlatform,
                                            listPlatformText: displayValue(nextListPlatform),
                                        },
                                        resolve,
                                        reject,
                                    });
                                });
                            }

                            const result = await PutData(payload, `broylesllc/catalog-items/${newRow.id}`);
                            if (!result.success) {
                                const message = result?.data?.message || "Save failed.";
                                setSnack({ open: true, color: "error", message });
                                throw new Error(message);
                            }
                            setSnack({ open: true, color: "success", message: "Saved." });
                            refetch();
                            return {
                                ...newRow,
                                listPlatform: nextListPlatform,
                                listPlatformText: displayValue(nextListPlatform),
                            };
                        }}
                        onProcessRowUpdateError={() => {}}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100, 500]}
                        slotProps={gridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: { borderRadius: "14px" },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {editingId ? "Edit Catalog Item" : "Add Catalog Item"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        <TextField
                            label="Item ID"
                            size="small"
                            value={form.itemId}
                            onChange={setField("itemId")}
                            fullWidth
                            required
                        />
                        <Autocomplete
                            size="small"
                            loading={Boolean(fetchingSkus || loadingMoreSkus)}
                            loadingText="Searching SKUs…"
                            noOptionsText={
                                skuQuery.trim() ? "No SKUs found" : "Type to search SKUs"
                            }
                            options={skuOptions}
                            value={form.product}
                            onChange={(_event, next) => applySkuSelection(next)}
                            onInputChange={(_event, value, reason) => {
                                if (reason === "input") setSkuQuery(value);
                                if (reason === "clear") setSkuQuery("");
                            }}
                            getOptionLabel={(option) =>
                                option?.listingField ||
                                option?.itemName ||
                                option?.internalSkuId ||
                                ""
                            }
                            isOptionEqualToValue={(option, value) =>
                                String(option?._id) === String(value?._id)
                            }
                            filterOptions={(options) => options}
                            ListboxProps={skuListboxProps}
                            renderOption={renderSkuOption}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="SKU Name"
                                    placeholder="Search products to link"
                                    helperText={
                                        skuHelperText ||
                                        "Linked SKU List record. Clear to unlink."
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {fetchingSkus || loadingMoreSkus ? (
                                                    <CircularProgress color="inherit" size={18} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Autocomplete
                            size="small"
                            options={shelfChoices}
                            value={shelfChoices.find((option) => option.id === form.shelfId) || null}
                            getOptionLabel={(option) => option?.shelfName || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_event, next) => {
                                setShelfTouched(true);
                                setForm((prev) => ({
                                    ...prev,
                                    shelfId: next?.id || "",
                                    shelfBinId: "",
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Shelf"
                                    placeholder="Choose a shelf"
                                    required={
                                        form.status === "Listed" &&
                                        (!editingId ||
                                            editingOriginalStatus.current !== "Listed")
                                    }
                                    error={
                                        form.status === "Listed" &&
                                        !formHasShelf(form) &&
                                        (!editingId ||
                                            editingOriginalStatus.current !== "Listed")
                                    }
                                    helperText={
                                        form.status === "Listed" && !formHasShelf(form)
                                            ? editingId &&
                                              editingOriginalStatus.current === "Listed"
                                                ? "Listed with no shelf — you can still save; add a shelf when you can."
                                                : "A shelf location is required when setting status to Listed."
                                            : editingId && !form.shelfId && form.shelves
                                              ? `Currently on ${form.shelves}. Choosing a shelf moves this item there.`
                                              : "Clear this to take the item off its shelf."
                                    }
                                />
                            )}
                        />
                        {formShelfBins.length > 0 ? (
                            <Autocomplete
                                size="small"
                                options={formShelfBins}
                                value={
                                    formShelfBins.find((bin) => bin.id === form.shelfBinId) || null
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_event, next) => {
                                    setShelfTouched(true);
                                    setForm((prev) => ({
                                        ...prev,
                                        shelfBinId: next?.id || "",
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Bin"
                                        placeholder="Choose a bin"
                                        helperText="Optional. Bins configured on this shelf."
                                    />
                                )}
                            />
                        ) : null}
                        <SingleSelectField
                            label="Platform"
                            value={form.platform}
                            onChange={setSelect("platform")}
                            choices={catalogMeta.platform?.choices || []}
                        />
                        <SingleSelectField
                            label="Status"
                            value={form.status}
                            onChange={setSelect("status")}
                            choices={catalogMeta.status?.choices || []}
                        />
                        <SingleSelectField
                            label="Product Grade"
                            value={form.productGrade}
                            onChange={setSelect("productGrade")}
                            choices={catalogMeta.productGrade?.choices || []}
                        />
                        <MultiSelectField
                            label="List Platform"
                            value={form.listPlatform}
                            onChange={setSelect("listPlatform")}
                            choices={catalogMeta.listPlatform?.choices || []}
                        />
                        <TextField
                            label="Parser Item ID"
                            size="small"
                            value={form.parserItemId}
                            onChange={setField("parserItemId")}
                            fullWidth
                        />
                        <TextField
                            label="MAC Item Sub ID"
                            size="small"
                            value={form.macItemSubId}
                            onChange={setField("macItemSubId")}
                            fullWidth
                        />
                        <TextField
                            label="Automated Invoice Item Name"
                            size="small"
                            value={form.automatedInvoiceItemName}
                            onChange={setField("automatedInvoiceItemName")}
                            fullWidth
                        />
                        <TextField
                            label="Auction Link"
                            size="small"
                            value={form.auctionLink}
                            onChange={setField("auctionLink")}
                            fullWidth
                        />
                        <TextField
                            label="ASIN Pull - Vista / MAC"
                            size="small"
                            value={form.asinPullVistaMac}
                            onChange={setField("asinPullVistaMac")}
                            fullWidth
                        />
                        <TextField
                            label="Amazon ASIN"
                            size="small"
                            value={form.amazonAsinFromBbskt}
                            onChange={setField("amazonAsinFromBbskt")}
                            fullWidth
                        />
                        <TextField
                            label="Base Cost"
                            size="small"
                            type="number"
                            value={form.baseCost}
                            onChange={setField("baseCost")}
                            fullWidth
                        />
                        <TextField
                            label="Purchase Date"
                            size="small"
                            type="date"
                            value={form.purchaseDate}
                            onChange={setField("purchaseDate")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Expiration Date"
                            size="small"
                            type="date"
                            value={form.expirationDate}
                            onChange={setField("expirationDate")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <LookupField
                            label="Date Listed"
                            value={form.dateListed}
                            source="Set when status becomes Listed"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.needsLabel}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            needsLabel: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Needs Label"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.wizardRts}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            wizardRts: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Wizard - RTS"
                        />
                        <TextField
                            label="Notes for Automation"
                            size="small"
                            value={form.notesForAutomation}
                            onChange={setField("notesForAutomation")}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                            Calculated from linked records
                        </Typography>
                        <LookupField label="Walmart SKU (from SKU)" value={form.walmartSkuFromSku} />
                        <LookupField label="ASIN (from SKU)" value={form.asinFromSku || form.lookupAsin} />
                        <LookupField label="Walmart Blocked (from SKU)" value={form.walmartBlockedFromSku || form.lookupWalmartBlocked} />
                        <LookupField label="SKU Listing Field" value={form.lookupListingField} />
                        <LookupField label="MSRP (from SKU)" value={form.lookupMsrp} />
                        <LookupField label="Max purchase price (from SKU)" value={form.maxPurchasePriceFromSku} />
                        <LookupField label="Cost rollup 1x" value={form.costRollup1x} />
                        <LookupField label="Cost rollup 2x" value={form.costRollup2x} />
                        <LookupField label="Cost rollup 3x" value={form.costRollup3x} />
                        <LookupField label="Cost rollup 4x" value={form.costRollup4x} />
                        <LookupField label="Platform fees" value={form.platformFees} />
                        <LookupField label="Total cost" value={form.totalCost} />
                        <LookupField label="ID formula" value={form.idFormula} />
                        <LookupField label="In stock" value={form.inStock} />
                        <LookupField label="Turn time" value={form.turnTime} />
                        <LookupField label="Purchasing flag" value={form.purchasingFlag} />
                        <LookupField label="Purchasing gap" value={form.purchasingGap} />
                        <LookupField label="Purchasing team cost" value={form.purchasingTeamClt} />
                        <LookupField label="Invoice Number" value={form.invoiceNumber} />
                        <LookupField
                            label="Bundled Into"
                            value={form.bundledIntoItemId}
                        />
                        <LookupField
                            label="Bundle Source Items"
                            value={form.bundleSourceItemIds}
                        />
                        <LookupField label="Upload user (from invoices)" value={form.uploadUserFromInvoices} />
                        <LookupField label="Order creation dates" value={form.orderCreationDatesFromOrders} />
                        <LookupField label="Listing field (from ASIN)" value={form.listingFieldFromAsinLinked} />
                        <LookupField label="ASIN combo" value={form.asinComboBbsktAgent} />
                        <LookupField label="Walmart flag" value={form.walmartFlagField} />
                        <LookupField label="Shelf location rollup" value={form.shelfLocationNameRollup} />
                        <LookupField label="Days purchase to process" value={form.durationPurchaseToProcess} />
                        <LookupField label="Days purchase to sale" value={form.durationPurchasingToSale} />
                        <TextField
                            label="Notes"
                            size="small"
                            value={form.notes}
                            onChange={setField("notes")}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={save}
                        startIcon={
                            saving ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={bulkOpen}
                onClose={closeBulkEdit}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                    Bulk Edit ({selectedIds.length} selected)
                </DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 2, color: "#5c6570", fontSize: 14 }}>
                        {bulkApply.createBundle
                            ? "Create a new bundle catalog item from the selection. Source items become Bundled."
                            : "Check the fields to update. Unchecked fields stay unchanged."}
                        {bulkSaving
                            ? " Keep this dialog open until the update finishes."
                            : ""}
                    </Typography>
                    {bulkProgress ? (
                        <Typography sx={{ mb: 2, fontWeight: 600, fontSize: 14 }}>
                            {bulkApply.createBundle
                                ? "Creating bundle…"
                                : `Updating ${bulkProgress.done} of ${bulkProgress.total}…`}
                        </Typography>
                    ) : null}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={bulkApply.createBundle}
                                        disabled={bulkSaving}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setBulkApply((prev) => ({
                                                ...prev,
                                                createBundle: checked,
                                                ...(checked
                                                    ? {
                                                          shelf: false,
                                                          status: false,
                                                          listPlatform: false,
                                                      }
                                                    : {}),
                                            }));
                                            if (checked) {
                                                setSkuQuery("");
                                                setDebouncedSkuQuery("");
                                            } else {
                                                setBulkForm((prev) => ({
                                                    ...prev,
                                                    product: null,
                                                }));
                                            }
                                        }}
                                        sx={{
                                            color: "#94a3b8",
                                            "&.Mui-checked": { color: "#0d9488" },
                                        }}
                                    />
                                }
                                label="Create new bundle"
                            />
                            <Autocomplete
                                size="small"
                                disabled={bulkSaving || !bulkApply.createBundle}
                                loading={Boolean(fetchingSkus || loadingMoreSkus)}
                                loadingText="Searching SKUs…"
                                noOptionsText={
                                    skuQuery.trim()
                                        ? "No SKUs found"
                                        : "Type to search SKUs"
                                }
                                options={skuOptions}
                                value={bulkForm.product}
                                onChange={(_event, next) =>
                                    setBulkForm((prev) => ({
                                        ...prev,
                                        product: next
                                            ? {
                                                  _id: next._id,
                                                  listingField: next.listingField || "",
                                                  internalSkuId:
                                                      next.internalSkuId || "",
                                                  itemName: next.itemName || "",
                                                  asin: next.asin || "",
                                              }
                                            : null,
                                    }))
                                }
                                onInputChange={(_event, value, reason) => {
                                    if (reason === "input") setSkuQuery(value);
                                    if (reason === "clear") setSkuQuery("");
                                }}
                                getOptionLabel={(option) =>
                                    option?.listingField ||
                                    option?.itemName ||
                                    option?.internalSkuId ||
                                    ""
                                }
                                isOptionEqualToValue={(option, value) =>
                                    String(option?._id) === String(value?._id)
                                }
                                filterOptions={(options) => options}
                                ListboxProps={skuListboxProps}
                                renderOption={renderSkuOption}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Product SKU"
                                        placeholder="Search products to link"
                                        required={bulkApply.createBundle}
                                        helperText={
                                            bulkApply.createBundle
                                                ? skuHelperText ||
                                                  (selectedIds.length < 2
                                                      ? "Select at least two items."
                                                      : "Required. Applied to the new bundle item.")
                                                : " "
                                        }
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {fetchingSkus || loadingMoreSkus ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={18}
                                                        />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={bulkApply.shelf}
                                        disabled={bulkSaving || bulkApply.createBundle}
                                        onChange={(e) =>
                                            setBulkApply((prev) => ({
                                                ...prev,
                                                shelf: e.target.checked,
                                            }))
                                        }
                                        sx={{
                                            color: "#94a3b8",
                                            "&.Mui-checked": { color: "#0d9488" },
                                        }}
                                    />
                                }
                                label="Shelf"
                            />
                            <Autocomplete
                                size="small"
                                disabled={
                                    bulkSaving ||
                                    !bulkApply.shelf ||
                                    bulkApply.createBundle
                                }
                                options={shelfChoices}
                                value={
                                    shelfChoices.find(
                                        (option) => option.id === bulkForm.shelfId
                                    ) || null
                                }
                                getOptionLabel={(option) => option?.shelfName || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                onChange={(_event, next) =>
                                    setBulkForm((prev) => ({
                                        ...prev,
                                        shelfId: next?.id || "",
                                        shelfBinId: "",
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Shelf"
                                        placeholder="Choose a shelf"
                                        helperText={
                                            bulkApply.shelf
                                                ? "Clear the value to remove selected items from their shelves."
                                                : " "
                                        }
                                        required={
                                            bulkApply.status &&
                                            bulkForm.status === "Listed"
                                        }
                                    />
                                )}
                            />
                            {bulkApply.shelf && bulkShelfBins.length > 0 ? (
                                <Autocomplete
                                    size="small"
                                    sx={{ mt: 1.25 }}
                                    disabled={bulkSaving || bulkApply.createBundle}
                                    options={bulkShelfBins}
                                    value={
                                        bulkShelfBins.find(
                                            (bin) => bin.id === bulkForm.shelfBinId
                                        ) || null
                                    }
                                    getOptionLabel={(option) => option?.name || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    onChange={(_event, next) =>
                                        setBulkForm((prev) => ({
                                            ...prev,
                                            shelfBinId: next?.id || "",
                                        }))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Bin"
                                            placeholder="Choose a bin"
                                            helperText="Optional. Applied with the shelf to all selected items."
                                        />
                                    )}
                                />
                            ) : null}
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={bulkApply.status}
                                        disabled={bulkSaving || bulkApply.createBundle}
                                        onChange={(e) =>
                                            setBulkApply((prev) => ({
                                                ...prev,
                                                status: e.target.checked,
                                            }))
                                        }
                                        sx={{
                                            color: "#94a3b8",
                                            "&.Mui-checked": { color: "#0d9488" },
                                        }}
                                    />
                                }
                                label="Status"
                            />
                            <SingleSelectField
                                label="Status"
                                value={bulkForm.status}
                                onChange={(value) =>
                                    setBulkForm((prev) => ({ ...prev, status: value }))
                                }
                                choices={catalogMeta.status?.choices || []}
                                disabled={
                                    bulkSaving ||
                                    !bulkApply.status ||
                                    bulkApply.createBundle
                                }
                                hint={
                                    bulkApply.status && bulkForm.status === "Listed"
                                        ? "Listed requires a shelf on every selected item."
                                        : undefined
                                }
                            />
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={bulkApply.listPlatform}
                                        disabled={bulkSaving || bulkApply.createBundle}
                                        onChange={(e) =>
                                            setBulkApply((prev) => ({
                                                ...prev,
                                                listPlatform: e.target.checked,
                                            }))
                                        }
                                        sx={{
                                            color: "#94a3b8",
                                            "&.Mui-checked": { color: "#0d9488" },
                                        }}
                                    />
                                }
                                label="List Platform"
                            />
                            <MultiSelectField
                                label="List Platform"
                                value={bulkForm.listPlatform}
                                onChange={(value) =>
                                    setBulkForm((prev) => ({
                                        ...prev,
                                        listPlatform: value,
                                    }))
                                }
                                choices={listPlatformChoices}
                                disabled={
                                    bulkSaving ||
                                    !bulkApply.listPlatform ||
                                    bulkApply.createBundle
                                }
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        onClick={closeBulkEdit}
                        disabled={bulkSaving}
                        sx={catalogQuietButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            bulkSaving ||
                            !selectedIds.length ||
                            (bulkApply.createBundle &&
                                (selectedIds.length < 2 || !bulkForm.product?._id))
                        }
                        onClick={saveBulk}
                        startIcon={
                            bulkSaving ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        {bulkApply.createBundle
                            ? bulkSaving
                                ? "Creating…"
                                : `Create bundle (${selectedIds.length})`
                            : bulkProgress
                              ? `Updating ${bulkProgress.done}/${bulkProgress.total}`
                              : `Apply to ${selectedIds.length}`}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(listedShelfPrompt)}
                onClose={() => {}}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                    Shelf required to list
                </DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 2, color: "#5c6570", fontSize: 14 }}>
                        <strong>{listedShelfPrompt?.itemLabel || "This item"}</strong> has no
                        shelf location. Choose a shelf before setting status to Listed.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Autocomplete
                            size="small"
                            options={shelfChoices}
                            value={
                                shelfChoices.find((option) => option.id === listedShelfId) || null
                            }
                            getOptionLabel={(option) => option?.shelfName || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_event, next) => {
                                setListedShelfId(next?.id || "");
                                setListedShelfBinId("");
                                setListedShelfError("");
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Shelf"
                                    placeholder="Choose a shelf"
                                    required
                                    error={Boolean(listedShelfError)}
                                    helperText={listedShelfError || "Required to continue"}
                                    autoFocus
                                />
                            )}
                        />
                        {listedShelfBins.length > 0 ? (
                            <Autocomplete
                                size="small"
                                options={listedShelfBins}
                                value={
                                    listedShelfBins.find((bin) => bin.id === listedShelfBinId) ||
                                    null
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_event, next) => {
                                    setListedShelfBinId(next?.id || "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Bin"
                                        placeholder="Choose a bin"
                                        helperText="Optional. Bins configured on this shelf."
                                    />
                                )}
                            />
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        onClick={() => {
                            const prompt = listedShelfPrompt;
                            setListedShelfPrompt(null);
                            setListedShelfId("");
                            setListedShelfBinId("");
                            setListedShelfError("");
                            prompt?.reject?.(new Error("Shelf required"));
                        }}
                        disabled={listedShelfSaving}
                        sx={catalogQuietButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={listedShelfSaving}
                        onClick={async () => {
                            if (!listedShelfId) {
                                setListedShelfError("Choose a shelf location to continue.");
                                return;
                            }
                            const prompt = listedShelfPrompt;
                            if (!prompt) return;
                            setListedShelfSaving(true);
                            const result = await PutData(
                                {
                                    ...prompt.pendingPayload,
                                    shelfId: listedShelfId,
                                    shelfBinId: listedShelfBinId || null,
                                },
                                `broylesllc/catalog-items/${prompt.catalogItemId}`
                            );
                            setListedShelfSaving(false);
                            if (!result.success) {
                                const message =
                                    result?.data?.message ||
                                    "Could not save Listed status with shelf.";
                                setListedShelfError(message);
                                setSnack({ open: true, color: "error", message });
                                return;
                            }
                            const shelfName =
                                shelfChoices.find((option) => option.id === listedShelfId)
                                    ?.shelfName || "";
                            const binName =
                                listedShelfBins.find((bin) => bin.id === listedShelfBinId)
                                    ?.name || "";
                            setListedShelfPrompt(null);
                            setListedShelfId("");
                            setListedShelfBinId("");
                            setListedShelfError("");
                            setSnack({ open: true, color: "success", message: "Saved." });
                            refetch();
                            prompt.resolve?.({
                                ...prompt.pendingRow,
                                status: "Listed",
                                shelfIds: [listedShelfId],
                                shelves: shelfName,
                                shelfBinId: listedShelfBinId || "",
                                shelfBinName: binName,
                            });
                        }}
                        startIcon={
                            listedShelfSaving ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Save with shelf
                    </Button>
                </DialogActions>
            </Dialog>

            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? "Delete catalog item?"
                        : `Delete ${deleteTargets.length} catalog items?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            <Dialog
                open={linkSku.open}
                onClose={() => closeLinkSku()}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {linkSku.product ? "Change SKU" : "Link SKU"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        {linkSku.itemId ? (
                            <Typography sx={{ fontSize: 13, color: "#5c6570" }}>
                                Item ID{" "}
                                <Box component="span" sx={{ fontWeight: 700, color: "#1c2430" }}>
                                    {linkSku.itemId}
                                </Box>
                            </Typography>
                        ) : null}
                        <Autocomplete
                            size="small"
                            autoFocus
                            openOnFocus
                            loading={Boolean(fetchingSkus || loadingMoreSkus)}
                            loadingText="Searching SKUs…"
                            noOptionsText={
                                skuQuery.trim() ? "No SKUs found" : "Type to search SKUs"
                            }
                            options={skuOptions}
                            value={linkSku.product}
                            onChange={(_event, next) =>
                                setLinkSku((prev) => ({
                                    ...prev,
                                    product: next
                                        ? {
                                              _id: next._id,
                                              listingField: next.listingField || "",
                                              internalSkuId: next.internalSkuId || "",
                                              itemName: next.itemName || "",
                                              asin: next.asin || "",
                                          }
                                        : null,
                                }))
                            }
                            onInputChange={(_event, value, reason) => {
                                if (reason === "input") setSkuQuery(value);
                                if (reason === "clear") setSkuQuery("");
                            }}
                            getOptionLabel={(option) =>
                                option?.listingField ||
                                option?.itemName ||
                                option?.internalSkuId ||
                                ""
                            }
                            isOptionEqualToValue={(option, value) =>
                                String(option?._id) === String(value?._id)
                            }
                            filterOptions={(options) => options}
                            ListboxProps={skuListboxProps}
                            renderOption={renderSkuOption}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="SKU Name"
                                    placeholder="Search products to link"
                                    helperText={
                                        skuHelperText ||
                                        "Search by listing field, internal SKU, or ASIN. Clear to unlink."
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {fetchingSkus || loadingMoreSkus ? (
                                                    <CircularProgress color="inherit" size={18} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={closeLinkSku} disabled={linkSkuSaving} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={linkSkuSaving}
                        onClick={saveLinkSku}
                        startIcon={
                            linkSkuSaving ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <SkuDetailDialog
                open={skuDialog.open}
                onClose={() =>
                    setSkuDialog({
                        open: false,
                        productId: null,
                        product: null,
                        variant: null,
                    })
                }
                productId={skuDialog.productId}
                product={skuDialog.product}
                variant={skuDialog.variant}
            />
        </>
    );
}
